import { diff as deepDiff, Diff } from "deep-diff";
import { DataProvider } from "./DataProvider";

export class Change<T> {
    private readonly _diff: Diff<T>[];
    public readonly oldValue: T;
    public readonly newValue: T;

    constructor(diff: Diff<T>[], oldValue: T, newValue: T) {
        this._diff = diff;
        this.oldValue = oldValue;
        this.newValue = newValue;
    }

    /** Returns True if the change contains a change to a property */
    public includes(path: string) {
        const a = path.split(".");
        for (const d of this._diff) {
            for (let i = 0; i < a.length; ++i) {
                if (d.path && d.path[i] !== a[i]) {
                    return false;
                }
            }
        }
        return true;
    }
}

export class ChangeSet<T> {
    public created: T[] = [];
    public deleted: T[] = [];
    public updated: Change<T>[] = [];

    constructor();
    constructor(created: T[], deleted: T[], updated: Change<T>[]);
    constructor(...args: any[]) {
        if (args.length > 0) {
            if (args.length > 3) {
                throw new Error("Invalid number of parameters");
            }

            this.created = args[0];
            this.deleted = args[1];
            this.updated = args[2];
        }
    }

    hasChanges() {
        return this.created.length + this.deleted.length + this.updated.length > 0;
    }

    public toString(): string {
        return `created ${this.created.length}, deleted ${this.deleted.length}, updated ${this.updated.length}`;
    }
}

export interface ChangeSetListener {
    onChangeSet(provider: DataProvider, changeSet: ChangeSet<any>): void;
}

export function createChangeSet<T extends { id: string | undefined }>(
    existingItems: T[],
    newItems: T[],
    existingItemLookup?: Map<string, T> | undefined
): ChangeSet<T> {
    // Assume all are unreferenced and remove entries as they are processed;
    const created: T[] = [];
    const updated: Change<T>[] = [];
    const deleted = new Set<string>(existingItems?.map((x) => x.id as string));

    if (!existingItemLookup) {
        existingItemLookup = new Map<string, T>(existingItems.map((x) => [x.id as string, x]));
    }

    for (const item of newItems) {
        // mark used
        deleted.delete(item.id as string);
        const currentObject = existingItemLookup.get(item.id as string);
        if (!currentObject) {
            created.push(item);
        } else {
            const diff = deepDiff(currentObject, item);
            if (diff && diff.length > 0) {
                updated.push(new Change(diff, currentObject, item));
            }
        }
    }
    return new ChangeSet(
        created,
        Object.values(deleted).map((x) => x),
        updated
    );
}

export function filterChangeSetByProps<T>(changeSet: ChangeSet<T>, paths: string[]): ChangeSet<T> {
    const updated = changeSet.updated.filter((change) => {
        for (const path of paths) {
            return change.includes(path);
        }
        return false;
    });

    return new ChangeSet(changeSet.created, changeSet.deleted, updated);
}
